import HttpResponse, { ErrorResponse } from './HttpResponse';
import { getAuthToken } from '../utils/httpClientUtil';
import { sparePartService } from '../services';
import { navigate } from 'gatsby';
/* globals CSS_CONFIG */

interface BaseConfig {
  method: 'GET' | 'POST' | 'PUT' | 'HEAD';
  headers: Headers;
  body?: string;
}

interface RequestConfig extends BaseConfig {
  credentials: string;
}

export interface FetchRequestConfig extends BaseConfig {
  credentials: 'include';
}

const ensureTrailingSlash = (url: string): string => url.replace(/\/?$/, '/');
const removeLeadingSlash = (url: string): string => {
  const [slash, ...rest] = url;
  return slash === '/' ? rest.toString() : url;
};

interface HeaderMethodType {
  'Akamai-BM-Telemetry': string;
}

const getHeaderForMethod = (requestType: string = 'GET'): HeaderMethodType | {} => {
  return ['POST', 'OPTIONS'].includes(requestType) ? {
    'Akamai-BM-Telemetry': window?.bmak?.get_telemetry()
  }: {};
}

const fetchRequest = async (
  url: string,
  config: RequestConfig
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
Promise<any> => {
  try {
    const response = await fetch(url, config as FetchRequestConfig);

    if (response.status < 200 || response.status >= 300) {
      const error: ErrorResponse = {
        status: response.status,
        data: await response.json(),
      };
      return new HttpResponse(null, error);
    }

    const result = await response.json();
    return new HttpResponse(result, undefined, response.status);
  } catch (e) {
    const error: ErrorResponse = {
      status: 500,
      data: e,
    };
    return new HttpResponse(null, error);
  }
};

class HttpClient {
  private baseUrl: string = ensureTrailingSlash(CSS_CONFIG.SPAREPARTAPIHOST);
  private verifyBackendAvailability: boolean;

  constructor(verifyBackendAvailability: boolean = true) {
    this.verifyBackendAvailability = verifyBackendAvailability;
  }

  public getHeader(requestType: string = 'GET'): FetchRequestConfig {
    return {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Accept-Language': `${CSS_CONFIG.LANGUAGE}-${CSS_CONFIG.COUNTRY}`,
        'Content-Language': `${CSS_CONFIG.LANGUAGE}-${CSS_CONFIG.COUNTRY}`,
        ...getHeaderForMethod(requestType),
        ...getAuthToken(),
      }),
    };
  };

  public formatUrl = (url: string): string => {
    return this.baseUrl + removeLeadingSlash(url);
  };

  public head(url: string): Promise<any> {
    const finalUrl = this.baseUrl + removeLeadingSlash(url);
    const finalConfig: RequestConfig = {
      ...this.getHeader('HEAD'),
      method: 'HEAD',
    };
    return fetchRequest(finalUrl, finalConfig);
  }

  public verifyBackend = async (url: string = '/greeting'): Promise<any> => {
    const { response, error, status } = await this.head(url);
    if (!status || error || status != 200) {
      navigate('/503');
      return { response, error, status };
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public get = async (url: string): Promise<any> => {
    const finalUrl = this.baseUrl + removeLeadingSlash(url);
    const finalConfig: RequestConfig = {
      ...this.getHeader('GET'),
    };
    return fetchRequest(finalUrl, finalConfig);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public post = async (url: string, customerData: {}): Promise<any> => {
    const finalUrl = this.baseUrl + removeLeadingSlash(url);
    const finalConfig: RequestConfig = {
      ...this.getHeader('POST'),
      method: 'POST',
      body: JSON.stringify({ ...customerData }),
    };
    return fetchRequest(finalUrl, finalConfig);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public put = async (url: string, customerData: {}): Promise<any> => {
    const finalUrl = this.baseUrl + removeLeadingSlash(url);
    const finalConfig: RequestConfig = {
      ...this.getHeader('PUT'),
      method: 'PUT',
      body: JSON.stringify({ ...customerData }),
    };
    return fetchRequest(finalUrl, finalConfig);
  };
}

export default HttpClient;
